import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';

// Kassir / admin: «🍽️ Banket menyu» — aktiv banket menyusunu A4-də N vərəq çap et.
// Vərəq BanquetPrint səhifəsində (gizli iframe, ?embed=1) qurulur və A4-ə sığdırılır,
// HTML buraya qaytarılır → desktopda C#-a «print-a4» (copies) göndərilir.
// Web brauzerdə C# yoxdur → çap səhifəsi açılır (orada brauzerin çap pəncərəsi).

const hasWebView = () => typeof window !== 'undefined' && !!window.chrome?.webview?.postMessage;
const fmtPrice = (v) => { const n = Number(v || 0); return Number.isInteger(n) ? String(n) : n.toFixed(2); };

// Gizli iframe-də vərəqi qur, HTML-i al
const renderSheetHtml = (banquetId, variant, timeoutMs = 25000) => new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;left:-12000px;top:0;width:900px;height:1250px;border:0;opacity:0;pointer-events:none;';
    let done = false;
    const finish = (fn, v) => {
        if (done) return;
        done = true;
        window.removeEventListener('message', onMsg);
        try { iframe.remove(); } catch { /* ignore */ }
        fn(v);
    };
    const onMsg = (ev) => {
        if (ev.origin !== window.location.origin) return;
        const d = ev.data;
        if (!d || d.type !== 'bq-sheet-html' || String(d.id) !== String(banquetId)) return;
        if (d.error) finish(reject, new Error(d.error));
        else if (d.html) finish(resolve, d.html);
    };
    window.addEventListener('message', onMsg);
    setTimeout(() => finish(reject, new Error('Menyu hazırlanmadı (vaxt bitdi).')), timeoutMs);
    iframe.src = `/admin/banquet/${banquetId}/print?embed=1&variant=${variant}`;
    document.body.appendChild(iframe);
});

// C#-dən tək nəticəni gözlə
const waitWebView = (type, reqId, ms) => new Promise((resolve) => {
    const wv = window.chrome.webview;
    let done = false;
    const handler = (ev) => {
        const d = ev?.data;
        if (!d || d.type !== type) return;
        if (reqId && d.requestId && d.requestId !== reqId) return;
        done = true; wv.removeEventListener('message', handler); resolve(d);
    };
    wv.addEventListener('message', handler);
    setTimeout(() => { if (!done) { wv.removeEventListener('message', handler); resolve(null); } }, ms);
});

export default function BanquetPrintDialog({ open, onClose, banquets = [] }) {
    const desktop = hasWebView();
    const [printers, setPrinters] = useState([]);
    const [banquetId, setBanquetId] = useState(null);
    const [copies, setCopies] = useState('1');
    const [variant, setVariant] = useState('mono');
    const [printer, setPrinter] = useState('');
    const [busy, setBusy] = useState('');
    const copiesRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setBanquetId(banquets[0]?.id || null);
        setCopies('1');
        setVariant('mono');
        setBusy('');
        if (desktop) {
            api.get('/printers').then((r) => {
                const list = (r.data?.data || []).filter((p) => Number(p.active ?? 1) === 1);
                setPrinters(list);
                setPrinter(list.find((p) => p.printer_type === 'a4')?.name || '');
            }).catch(() => setPrinters([]));
        }
        setTimeout(() => copiesRef.current?.select(), 50);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const a4List = printers.filter((p) => p.printer_type === 'a4');
    const others = printers.filter((p) => p.printer_type !== 'a4');
    const n = Math.min(99, Math.max(1, parseInt(copies, 10) || 1));
    const sel = banquets.find((b) => b.id === banquetId);

    const doPrint = async () => {
        if (!banquetId || busy) return;
        if (!desktop) {
            // Brauzer: çap səhifəsi (orada «Çap et» brauzerin pəncərəsini açır)
            window.open(`/admin/banquet/${banquetId}/print?variant=${variant}`, '_blank');
            onClose();
            return;
        }
        if (!printer) { toast.error('Printer seçin.'); return; }
        setBusy('print');
        try {
            const html = await renderSheetHtml(banquetId, variant);
            const requestId = `bq_${Date.now()}`;
            const wait = waitWebView('print-a4-result', requestId, 60000);
            window.chrome.webview.postMessage({ type: 'print-a4', printer, copies: n, html, requestId, a4exact: true });
            toast(`🖨️ ${n} vərəq → ${printer}`, { duration: 2500 });
            onClose();
            const r = await wait;
            if (r && r.ok) toast.success(`✓ ${n} vərəq çapa göndərildi.`);
            else if (r) toast.error(r.error || `Çap alınmadı (${r.status || 'xəta'}).`);
        } catch (e) {
            toast.error(e.message || 'Çap alınmadı.');
        } finally {
            setBusy('');
        }
    };

    const doPdf = async () => {
        if (!banquetId || busy) return;
        setBusy('pdf');
        try {
            const html = await renderSheetHtml(banquetId, variant);
            const filename = `Banket_menyu_${fmtPrice(sel?.price_per_person)}AZN_${variant === 'color' ? 'rengli' : 'ag-qara'}.pdf`;
            const wait = waitWebView('export-pdf-result', '', 60000);
            window.chrome.webview.postMessage({ type: 'export-pdf', filename, html, a4exact: true });
            onClose();
            const r = await wait;
            if (r && r.ok) toast.success(`📄 PDF masaüstünə saxlanıldı: ${r.path || filename}`, { duration: 5000 });
            else toast.error(r?.error || 'PDF yaradılmadı.');
        } catch (e) {
            toast.error(e.message || 'PDF yaradılmadı.');
        } finally {
            setBusy('');
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-4" onMouseDown={() => !busy && onClose()}>
            <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onClose(); if (e.key === 'Enter') { e.preventDefault(); doPrint(); } }}>
                <div className="mb-4 text-lg font-black text-gray-800">🍽️ Banket menyusunu çap et</div>

                {banquets.length > 1 && (
                    <div className="mb-4">
                        <div className="mb-1.5 text-xs font-semibold text-gray-600">Banket</div>
                        <div className="space-y-1.5">
                            {banquets.map((b) => (
                                <button key={b.id} type="button" onClick={() => setBanquetId(b.id)}
                                    className={`flex w-full items-center justify-between rounded-xl border-2 px-3 py-2 text-left text-sm ${banquetId === b.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                    <span className="truncate font-bold text-gray-800">{b.name}</span>
                                    <span className="shrink-0 font-black text-primary-700">{fmtPrice(b.price_per_person)} ₼</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mb-1.5 text-xs font-semibold text-gray-600">Neçə vərəq?</div>
                <div className="mb-4 flex items-center gap-2">
                    <button type="button" onClick={() => setCopies(String(Math.max(1, n - 1)))}
                        className="h-11 w-11 rounded-xl border-2 border-gray-200 text-xl font-black text-gray-600 hover:bg-gray-50">−</button>
                    <input ref={copiesRef} type="number" min="1" max="99" value={copies}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setCopies(e.target.value.replace(/[^\d]/g, ''))}
                        className="h-11 w-20 rounded-xl border-2 border-primary-300 text-center text-xl font-black text-gray-800 focus:border-primary-500 focus:outline-none" />
                    <button type="button" onClick={() => setCopies(String(Math.min(99, n + 1)))}
                        className="h-11 w-11 rounded-xl border-2 border-gray-200 text-xl font-black text-gray-600 hover:bg-gray-50">+</button>
                </div>

                <div className="mb-1.5 text-xs font-semibold text-gray-600">Görünüş</div>
                <div className="mb-4 flex rounded-xl bg-gray-100 p-1">
                    {[['mono', 'Ağ-qara'], ['color', 'Rəngli']].map(([k, label]) => (
                        <button key={k} type="button" onClick={() => setVariant(k)}
                            className={`flex-1 rounded-lg py-2 text-sm font-bold ${variant === k ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}>{label}</button>
                    ))}
                </div>

                {desktop && (
                    <>
                        <div className="mb-1.5 text-xs font-semibold text-gray-600">Printer</div>
                        {a4List.length === 0 && (
                            <div className="mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                                📄 A4 printer tapılmadı. {printers.length ? 'Hansı printerdən çap edək?' : 'Printer yoxdur. PDF kimi saxlaya bilərsiniz.'}
                            </div>
                        )}
                        {printers.length > 0 && (
                            <select value={printer} onChange={(e) => setPrinter(e.target.value)}
                                className="mb-1 h-11 w-full rounded-xl border-2 border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 focus:border-primary-400 focus:outline-none">
                                {!printer && <option value="">Printer seçin...</option>}
                                {a4List.map((p) => <option key={p.id || p.name} value={p.name}>📄 {p.label || p.name}</option>)}
                                {others.length > 0 && (
                                    <optgroup label={a4List.length ? 'Digər printerlər' : 'Printerlər'}>
                                        {others.map((p) => <option key={`o_${p.id || p.name}`} value={p.name}>🧾 {p.label || p.name}</option>)}
                                    </optgroup>
                                )}
                            </select>
                        )}
                    </>
                )}

                <div className="mt-5 flex gap-2">
                    <button type="button" onClick={onClose} disabled={!!busy}
                        className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-50 disabled:opacity-50">Ləğv</button>
                    {desktop && (
                        <button type="button" onClick={doPdf} disabled={!!busy || !banquetId}
                            className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                            {busy === 'pdf' ? '...' : '📄 PDF'}
                        </button>
                    )}
                    <button type="button" onClick={doPrint} disabled={!!busy || !banquetId || (desktop && !printer)}
                        className="flex-[1.4] rounded-xl bg-gray-900 py-2.5 text-sm font-black text-white hover:bg-gray-800 disabled:opacity-40">
                        {busy === 'print' ? 'Hazırlanır...' : `🖨️ Çap et (${n})`}
                    </button>
                </div>
            </div>
        </div>
    );
}
